/**
 * Metric registry for the dashboard Compare view — generated from the shared VOLUUM_COLUMNS
 * catalog so it exposes EVERY tracker metric (impressions, CTRs, unique clicks, REG, FTD,
 * EPC/EPV, CPL/CPS…) for Voluum + Binom, plus the two SMS send metrics.
 * For Media Devoted | Created By Ciro Autiero
 */

import {
  VOLUUM_COLUMNS,
  type ColumnDef,
} from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab/shared/constants';

/** The bucket slot a metric is read from (`tresCommas` = the Tres Commas slot). */
export type MetricSource = 'voluum' | 'binom' | 'tresCommas' | 'sms';
export type MetricFormat = 'int' | 'currency' | 'percent' | 'decimal';
export type MetricGroup = 'Voluum' | 'Binom' | 'Tres Commas' | 'SMS';

export interface CompareMetric {
  key: string;
  label: string;
  shortLabel: string;
  group: MetricGroup;
  source: MetricSource;
  /** Voluum column key read off the bucket's metric record. */
  field: string;
  format: MetricFormat;
  /** true = up is good, false = down is good (cost), undefined = neutral (no color). */
  higherIsBetter?: boolean;
  colorToken: string;
}

function accentOf(col: ColumnDef): 'conv' | 'reg' | 'ftd' | undefined {
  return 'columnAccent' in col
    ? (col as { columnAccent?: 'conv' | 'reg' | 'ftd' }).columnAccent
    : undefined;
}

function senseOf(col: ColumnDef): boolean | undefined {
  if (col.colorHint === 'negative') return false;
  if (col.colorHint === 'money' || col.colorHint === 'metric' || accentOf(col)) return true;
  return undefined;
}

function tokenOf(col: ColumnDef): string {
  const accent = accentOf(col);
  if (accent === 'ftd') return '--stat-ftd-value';
  if (accent === 'reg') return '--stat-reg-value';
  if (accent === 'conv') return '--stat-conv-value';
  if (col.colorHint === 'money') return '--stat-profit-value';
  if (col.colorHint === 'negative') return '--stat-cost-value';
  return '--primary';
}

function trackerMetrics(source: Exclude<MetricSource, 'sms'>, group: MetricGroup): CompareMetric[] {
  return VOLUUM_COLUMNS.map((col) => ({
    key: `${source}.${col.key}`,
    label: `${group} ${col.label}`,
    shortLabel: col.label,
    group,
    source,
    field: col.key,
    format: col.format as MetricFormat,
    higherIsBetter: senseOf(col),
    colorToken: tokenOf(col),
  }));
}

export const COMPARE_METRICS: CompareMetric[] = [
  ...trackerMetrics('voluum', 'Voluum'),
  ...trackerMetrics('binom', 'Binom'),
  { key: 'sms.sentCount', label: 'SMS Drops sent', shortLabel: 'Drops', group: 'SMS', source: 'sms', field: 'sentCount', format: 'int', colorToken: '--primary' },
  { key: 'sms.amount', label: 'SMS Volume', shortLabel: 'Volume', group: 'SMS', source: 'sms', field: 'amount', format: 'int', colorToken: '--stat-reg-value' },
];

/**
 * Tres Commas metrics — the SMSWiser country trend chart only. Kept OUT of
 * COMPARE_METRICS so the dashboard Compare view's picker stays Voluum + Binom.
 */
export const TRES_COMMAS_COMPARE_METRICS: CompareMetric[] = trackerMetrics('tresCommas', 'Tres Commas');

export const COMPARE_METRIC_BY_KEY: Record<string, CompareMetric> = Object.fromEntries(
  COMPARE_METRICS.map((m) => [m.key, m]),
);

/** Metrics selected by default (Voluum headline set; FTD = customConversions7). */
export const DEFAULT_METRIC_KEYS = [
  'voluum.customConversions7',
  'voluum.profit',
  'voluum.revenue',
  'voluum.cost',
  'voluum.roi',
];
